import { addBreadcrumb } from '@sentry/react';
import { AuthenticationResponseJSON } from '@simplewebauthn/typescript-types';
import { AxiosProgressEvent } from 'axios';
import axiosRetry from 'axios-retry';
import { replace } from 'connected-react-router';
import type { MemoryHistory } from 'history';
import { i18n as I18n } from 'i18next';
import { fromPairs, isEmpty, map } from 'lodash/fp';
import { SubmissionError } from 'redux-form';

import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { ProductFeature } from 'inquiry/lib/ProductFeatures';
import { VERIFY_WITH_PERSONA_MESSAGES } from 'inquiry/lib/reusablePersona';
import { TransitionParams, WorkflowInitialVariables } from 'inquiry/lib/types';
import {
  incrementPollerId,
  setTransitionStart,
  setUiBlocked,
  setUiReady,
} from 'inquiry/redux/actions/ui';
import { ApiOptions } from 'lib/clients/ApiClient';
import EventsClient from 'lib/clients/EventsClient';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiEndpoint, ApiModel, ApiPayload, ApiResource, ApiResponse } from 'lib/interfaces/api';
import ClientError from 'lib/interfaces/ClientError';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import type { PageTransition } from 'lib/interfaces/ThemeSerialized';
import { isUiParentComponent, UiComponent, UiComponentType } from 'lib/interfaces/ui-components';
import { captureException, captureMessage } from 'lib/reporting';
import { Json } from 'lib/types';
import { computeBrowserFingerprint, computeDeviceFingerprint } from 'lib/utils/browserFingerprint';
import { FromWidgetEvent, HolderClient } from 'verify/lib/clients/HolderClient';
import SessionClientVerify, { InquirySessionError } from 'verify/lib/clients/SessionClientVerify';
import { truncateMemoryHistory } from 'verify/lib/history';
import FlowType from 'verify/lib/interfaces/FlowType';
import InquiryParams from 'verify/lib/interfaces/InquiryParams';
import { setIdOnReporters } from 'verify/lib/reporting';
import { getPollingMode, Inquiry, PollingMode, ReusablePersonaStatus } from 'verify/models/inquiry';
import {
  InquirySession,
  InquirySessionAttributes,
  InquirySessionRelationships,
} from 'verify/models/inquiry-session';
import { InquiryTemplateVersion } from 'verify/models/inquiry-template-version';
import { Organization } from 'verify/models/organization';
import {
  logCollectedData,
  saveFeatureFlags,
  saveInquiry,
  saveInquiryTemplateVersion,
  saveOrganization,
  saveProductFeatures,
  saveTheme,
  setComponent,
  setCurrentSession,
} from 'verify/redux/actions/inquiry';
import { showMessage } from 'verify/redux/actions/messages';
import { RootState } from 'verify/redux/store';
import { loadInquiryFromCode } from 'verify/redux/thunk-actions/inquiry';
import { VerifyThunkAction } from 'verify/redux/types';

import { setNextInquiry } from '../slices/verify-with-persona';

/*
The following is a general visualization of inquiry control flow.
NOTE: currently only createInquiry is used in this demo. The other two
entry points are identical to v2 versions but just ignore some no longer
relevant logic.

loadInquiryFromCode──╮
                     │
  loadInquiryFromId──┼─>setupInquiryFromExistingSession───>routeToNextStep
                     │
      createInquiry──╯

Compared to Inquiry 2.0:
1. There is much less routing logic because the route is always /inquiry/ui.
2. We serve the template info along with inquiry because the payload is small. This simplifies fetches.

TODO: [PER-6006] copy thunk-actions over from front-end/verify/redux/thunk-actions/inquiry.ts
We shouldn't need to copy anything besides dependencies of loadInquiryFromCode/loadInquiryFromId.
*/

/**
 * @name loadInquiryFromCode
 *
 * Unchanged from 2.0 besides the final call to setupInquiryFromExistingSession.
 *
 * Used when there is a OTL code present. Creates an inquiry session and then
 * loads the inquiry. Ends with `setupInquiryFromExistingSession`.
 */

/**
 * @name loadInquiryFromId
 *
 * Unchanged from 2.0 besides the final call to setupInquiryFromExistingSession.
 *
 * Used when there is an inquiry ID (e.g. from share link). Simply loads the
 * inquiry and continues. Ends with `setupInquiryFromExistingSession`.
 */

const uiStepAllComponents = (step: StepUi | undefined): UiComponent[] =>
  step != null ? uiStepComponentsRecursive(step.config.components) : [];

const uiStepComponentsRecursive = (components: UiComponent[]): UiComponent[] =>
  components.flatMap((component) =>
    isUiParentComponent(component)
      ? [component, ...uiStepComponentsRecursive(component.attributes.children)]
      : [component],
  );

export function loadVerifyWithPersonaPopupFromCode(
  i18n: I18n,
  code: string | undefined,
  component: string | undefined,
): VerifyThunkAction {
  return async (dispatch, getState) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await dispatch(loadInquiryFromCode(i18n, code));

    const inquiry = getState().inquiry.inquiry;

    if (!inquiry) {
      dispatch(replace('/verify/invalid/code'));
      return;
    }

    const nextStep = inquiry.attributes?.['next-step'];

    if (nextStep?.type !== UiStepType.Ui) {
      dispatch(
        replace('/verify/invalid/config', {
          error: new Error('verify with persona popup accessed on non-ui step'),
        }),
      );
      return;
    }

    const verifyWithPersonaButton = uiStepAllComponents(nextStep).find(
      (c) => c.type === UiComponentType.ButtonVerifyWithPersona && c.name === component,
    );

    if (!verifyWithPersonaButton) {
      dispatch(
        replace('/verify/invalid/config', {
          error: new Error(
            'verify with persona popup accessed without verify with persona component',
          ),
        }),
      );
      return;
    }

    dispatch(setComponent(verifyWithPersonaButton));
    dispatch(replace('/inquiry/verify-with-persona/passkey-authentication'));
  };
}

export function loadCreatePersonaPopupFromCode(
  i18n: I18n,
  code: string | undefined,
  component: string | undefined,
): VerifyThunkAction {
  return async (dispatch, getState) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await dispatch(loadInquiryFromCode(i18n, code));

    const inquiry = getState().inquiry.inquiry;

    if (!inquiry) {
      dispatch(replace('/verify/invalid/code'));
      return;
    }

    const nextStep = inquiry.attributes?.['next-step'];

    if (nextStep?.type !== UiStepType.Ui) {
      dispatch(
        replace('/verify/invalid/config', {
          error: new Error('create persona popup accessed on non-ui step'),
        }),
      );
      return;
    }

    const createPersonaButton = uiStepAllComponents(nextStep).find(
      (c) => c.type === UiComponentType.ButtonCreatePersona && c.name === component,
    );

    if (!createPersonaButton) {
      dispatch(
        replace('/verify/invalid/config', {
          error: new Error('create persona popup accessed without create persona component'),
        }),
      );
      return;
    }

    dispatch(setComponent(createPersonaButton));
    dispatch(replace('/inquiry/create-persona/passkey-registration'));
  };
}

interface CreateInquiryPayload {
  type: ApiModel.Inquiry;
  attributes: Pick<
    InquiryParams,
    | 'inquiry-template-id'
    | 'inquiry-template-version-id'
    | 'environment'
    | 'environment-id'
    | 'account-id'
    | 'account-type-id'
    | 'reference-id'
    | 'redirect-uri'
    | 'theme-set-id'
    // @deprecated -- use theme-set-id
    | 'theme-id'
    | 'fields'
    | 'flow-type'
  > & { 'accepted-policies': boolean };
}

/**
 * @name createInquiry
 *
 * This handles initial setup of the inquiry if one doesn't already exist.
 * Previously named `startInquiry` which wasn't very descriptive.
 */
export function createInquiry(
  i18n: I18n,
  inquiryParams: InquiryParams,
  host: string,
): VerifyThunkAction {
  return async (dispatch) => {
    try {
      const environment =
        host === 'sandbox.withpersona.com' ? 'sandbox' : inquiryParams.environment;

      const payload: CreateInquiryPayload = {
        attributes: {
          // Note that for compatibility reasons, we accept the ID as 'template-id'
          // regardless of if it's an inquiry template or a template.
          'inquiry-template-id':
            inquiryParams['inquiry-template-id'] ?? inquiryParams['template-id'],
          'inquiry-template-version-id': inquiryParams['inquiry-template-version-id'],
          'redirect-uri': inquiryParams['redirect-uri'],
          'account-id': inquiryParams['account-id'],
          'account-type-id': inquiryParams['account-type-id'],
          'reference-id': inquiryParams['reference-id'],
          'theme-set-id': inquiryParams['theme-set-id'],
          // TODO: remove once all theme set users are migrated off of theme-id
          'theme-id': inquiryParams['theme-id'],
          environment,
          'environment-id': inquiryParams['environment-id'],
          'flow-type': HolderClient.flowType,
          'accepted-policies': true,
          fields: inquiryParams['fields'],
        },
        type: ApiModel.Inquiry as const,
      };

      const inquiryResponse = await SessionClientVerify.postResourceCreate<
        Inquiry,
        CreateInquiryPayload
      >(payload, {
        retryOptions: {
          retryCondition: (error) =>
            axiosRetry.isNetworkError(error) || axiosRetry.isRetryableError(error),
        },
      });
      const inquiry: Inquiry = inquiryResponse.data;
      dispatch(saveInquiry(inquiry));

      const inquirySessionResponse = await createSession(inquiry);
      dispatch(setCurrentSession(inquirySessionResponse.data));

      setIdOnReporters(
        inquiry.id,
        inquirySessionResponse.data.id,
        inquiry.attributes.environment ?? 'undefined',
        inquiry.relationships['inquiry-template-version'].data.id,
        inquiry.relationships.organization.data.id,
        inquiryResponse.meta?.['feature-flags'] ?? {},
      );

      EventsClient.logAmplitude('inquiry.start', {
        inquiryId: inquiry.id,
      });
      HolderClient.onEvent(FromWidgetEvent.Start, { inquiryId: inquiry.id });

      dispatch(setupInquiryFromExistingSession(i18n, inquiryResponse, inquirySessionResponse));
    } catch (error) {
      if (error instanceof ApiResponseError) {
        if (error.errors?.[0].code === 'inactive_template_error') {
          onError({ status: 422, code: 'inactive_template' }, error);
          dispatch(replace('/inquiry/inactive'));
        } else if (error.status >= 500) {
          onError({ status: error.status, code: 'http_response' }, error);
          captureException(error, 'error', undefined, { functionName: 'createInquiry' });
          dispatch(replace('/verify/invalid/config', { error }));
        } else {
          onError({ status: 400, code: 'invalid_config' }, error);
          captureException(error, 'warning', undefined, { functionName: 'createInquiry' });
          dispatch(replace('/verify/invalid/config', { error }));
        }
      } else {
        // Not a network error
        if (error instanceof Error) {
          onError({ status: 0, code: 'application_error' }, error);
        } else {
          onError({ status: -1, code: 'unknown' });
        }
        captureException(error, 'error', undefined, { functionName: 'createInquiry' });
        dispatch(replace('/verify/invalid/config', { error }));
      }
    }
  };
}

/**
 * @name setupInquiryFromExistingSession
 *
 * Followup action that is called by `loadInquiryFromId` and
 * `loadInquiryFromCode`. Mainly just sets state.
 */
export function setupInquiryFromExistingSession(
  i18n: I18n,
  { data: inquiry, meta, included }: ApiResponse<Inquiry>,
  { data: inquirySession }: ApiResponse<InquirySession>,
): VerifyThunkAction {
  return async (dispatch) => {
    if (!SessionClientVerify.accessToken) {
      throw new Error('No access token set for inquiry:' + inquiry.id);
    }

    // Specify type because default type is for Dashboard feature flags
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const featureFlags: FeatureFlagsMap = meta?.['feature-flags'];
    const productFeatures = meta?.['product-features'];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const inquiryTemplateVersion = included.find(
      (res) => res.id === inquiry.relationships['inquiry-template-version'].data.id,
    ) as InquiryTemplateVersion;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const organization = included.find(
      (res) => res.id === inquiry.relationships.organization.data.id,
    ) as Organization;

    if (featureFlags) {
      dispatch(saveFeatureFlags(featureFlags));
    }
    if (productFeatures) {
      dispatch(saveProductFeatures(productFeatures));
    }
    dispatch(saveInquiryTemplateVersion(inquiryTemplateVersion));
    // TODO: [PER-6006] once migration is done, serialize old themes to the new format rather than vice-versa.
    dispatch(
      saveTheme({
        id: 'backserialized-inquiry-template-version-theme',
        type: ApiModel.Theme,
        attributes: inquiryTemplateVersion.attributes.theme,
      }),
    );
    dispatch(saveOrganization(organization));

    const allowDeviceFingerprint =
      !!productFeatures?.[ProductFeature.InquiriesEnableDeviceFingerprint];

    // Fire and forget -- do not block on fingerprinting if it lags or times out
    captureFingerprints(allowDeviceFingerprint, SessionClientVerify.fpjsCountry);

    const loadedLanguage = i18n.language;
    const language = await setInitialLanguage(
      i18n,
      inquiryTemplateVersion.attributes['enabled-locales'],
      inquirySession.attributes.locale,
    );

    // reload inquiry after setting language to get new translations
    if (language !== loadedLanguage) {
      ({ data: inquiry } = await SessionClientVerify.getResource<Inquiry>(
        ApiEndpoint.Inquiry,
        inquiry.id,
      ));
    }

    // This can happen if the user reloads the page while an inquiry is polling.
    const pollingMode = getPollingMode(inquiry);

    if (pollingMode === PollingMode.BLOCKING) {
      try {
        await dispatch(pollInquiry(inquiry));
      } catch (e) {
        dispatch(setUiReady());
        throw e;
      }
    } else {
      dispatch(routeToNextStep(inquiry, { skipAnimation: true }));
    }
  };
}

interface TransitionPayloadAttributes {
  componentParams: TransitionParams | '';
  'flow-type': FlowType;
}

export function transitionInquiry({
  inquiry,
  componentParams,
  fromComponent,
  workflowInitialVariables,
  contentType = 'multipart/form-data',
  onUploadProgress,
}: {
  inquiry: Inquiry;
  /**
   * '' is a valid transition param and represents an empty value when the
   * content type is multipart/form-data. {} is not a valid form data value.
   */
  componentParams: TransitionParams | '';
  fromComponent: string;
  workflowInitialVariables: WorkflowInitialVariables;
  /**
   * Currently allows for either multipart/form-data or application/json.
   * multipart/form-data is needed for file submission.
   * We may switch everythign to use multipart/form-data since ultmately, Inquiries is a form.
   */
  contentType?: ApiOptions['contentType'];
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  featureFlags?: FeatureFlagsMap;
}): VerifyThunkAction<Promise<void>> {
  return async (dispatch) => {
    dispatch(setTransitionStart());
    // Invalidate active pollers, if any
    dispatch(incrementPollerId());

    try {
      const options: ApiOptions = {
        meta: {
          'from-component': fromComponent,
          'from-step': inquiry.attributes['next-step'].name,
          'workflow-initial-variables': workflowInitialVariables,
        },
      };

      options['contentType'] = contentType;
      options['onUploadProgress'] = onUploadProgress;
      /**
       * This sends [''] for an empty array instead of omitting the key.
       * When the key is omitted, the server interprets the value as nil
       * which leads to conflicts with JSONlogic interpretation on the server,
       * as well as prevents empty submissions for an optional field from
       * overriding previously stored values on the field.
       */
      options['formDataAllowEmptyArrays'] = true;

      if (contentType === 'multipart/form-data' && isEmpty(componentParams)) {
        /**
         * {} is unrepresentable in multipart/form-data. Empty values are specified as ''.
         * The only thing this does is cause errors to be keyed correctly.
         * The server responds differently to old clients that don't send componentParams.
         * @see Api::Internal::Verify::V1::InquiriesController#jsonapi_attributes_component_params
         */
        componentParams = '';
      }

      const { data: nextInquiry } = await SessionClientVerify.postResourceAction<
        Inquiry,
        ApiPayload<TransitionPayloadAttributes, ApiModel.Inquiry>
      >(
        {
          id: inquiry.id,
          type: inquiry.type,
          attributes: {
            componentParams,
            'flow-type': HolderClient.flowType,
          },
        },
        'transition',
        options,
      );

      // log data for client-side collection
      if (HolderClient.inquiryParams['send-client-side-collected-data-in-events']) {
        dispatch(
          logCollectedData({
            name: inquiry.attributes['next-step'].name,
            type: UiStepType.Ui,
            data: componentParams || {},
          }),
        );
      }

      // We currently assume that all transitions that transition to the same
      // step NAME should poll. We don't examine the entire step payload because
      // the step payload can change even when we want to poll; for example,
      // submits cause the `prefill` attribute to change due to field values
      // changing.
      const pollingMode = getPollingMode(nextInquiry);

      if (pollingMode === PollingMode.BLOCKING) {
        await dispatch(pollInquiry(nextInquiry));
      } else {
        dispatch(setUiReady());
        dispatch(
          routeToNextStep(nextInquiry, {
            isSameStep:
              inquiry.attributes['next-step'].name === nextInquiry.attributes['next-step'].name,
          }),
        );
      }
    } catch (e) {
      dispatch(setUiReady());
      if (e instanceof ApiNetworkError) {
        // It's possible the request succeeded but the response dropped. This
        // may cause an InconsistentTransitionError down the line.
        // Attempt to resolve by refetching inquiry. If the request failed it
        // will be a no-op.
        const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
          ApiEndpoint.Inquiry,
          inquiry.id,
        );
        dispatch(routeToNextStep(reloadedInquiry));
        // Still show an error message because the problem may be unrelated.
        throw e;
      } else if (e instanceof ApiResponseError) {
        const code = e.errors?.[0].code;
        switch (e.status) {
          case 401: // Unauthorized
            // Do nothing because we expect to be redirected
            break;
          case 409: // Conflict -- inconsistent transition, etc.
            if (
              code === 'inconsistent_transition_error' ||
              code === 'transition_from_terminal_state_error'
            ) {
              // Client-server desync; resolve by refetching inquiry
              // Does not need session token provided; should be in SessionClientVerify already
              // Don't need to show an error message; should appear like transition succeeded to client
              const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                ApiEndpoint.Inquiry,
                inquiry.id,
              );
              dispatch(routeToNextStep(reloadedInquiry));
            } else {
              throw e;
            }
            break;
          default:
            throw e;
        }
      } else {
        throw e;
      }
    }
  };
}

interface TransitionWithFieldsPayloadAttributes {
  fields: TransitionParams;
  'flow-type': FlowType;
}

// this is essentially the same as transitionInquiry, but for Integration step type
export function transitionInquiryFromIntegrationStep(
  inquiry: Inquiry,
  fields: TransitionParams,
  fromComponent: string,
  workflowInitialVariables: WorkflowInitialVariables,
): VerifyThunkAction {
  return async (dispatch) => {
    dispatch(setTransitionStart());
    // Invalidate active pollers, if any
    dispatch(incrementPollerId());

    try {
      const { data: nextInquiry } = await SessionClientVerify.postResourceAction<
        Inquiry,
        ApiPayload<TransitionWithFieldsPayloadAttributes, ApiModel.Inquiry>
      >(
        {
          id: inquiry.id,
          type: inquiry.type,
          attributes: {
            fields: fields,
            'flow-type': HolderClient.flowType,
          },
        },
        'transition',
        {
          meta: {
            'from-component': fromComponent,
            'from-step': inquiry.attributes['next-step'].name,
            'workflow-initial-variables': workflowInitialVariables,
          },
        },
      );

      // log data for client-side collection
      if (HolderClient.inquiryParams['send-client-side-collected-data-in-events']) {
        dispatch(
          logCollectedData({
            name: inquiry.attributes['next-step'].name,
            type: UiStepType.Integration,
            data: fields,
          }),
        );
      }

      // We currently assume that all transitions that transition to the same
      // step NAME should poll. We don't examine the entire step payload because
      // the step payload can change even when we want to poll; for example,
      // submits cause the `prefill` attribute to change due to field values
      // changing.
      const pollingMode = getPollingMode(nextInquiry);

      if (pollingMode === PollingMode.BLOCKING) {
        await dispatch(pollInquiry(nextInquiry));
      } else {
        dispatch(setUiReady());
        dispatch(
          routeToNextStep(nextInquiry, {
            isSameStep:
              inquiry.attributes['next-step'].name === nextInquiry.attributes['next-step'].name,
          }),
        );
      }
    } catch (e) {
      dispatch(setUiReady());
      if (e instanceof ApiNetworkError) {
        // It's possible the request succeeded but the response dropped. This
        // may cause an InconsistentTransitionError down the line.
        // Attempt to resolve by refetching inquiry. If the request failed it
        // will be a no-op.
        const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
          ApiEndpoint.Inquiry,
          inquiry.id,
        );
        dispatch(routeToNextStep(reloadedInquiry));
        // Still show an error message because the problem may be unrelated.
        throw e;
      } else if (e instanceof ApiResponseError) {
        const code = e.errors?.[0].code;
        switch (e.status) {
          case 401: // Unauthorized
            // Expect to be redirected, do nothing
            break;
          case 409: // Conflict -- inconsistent transition, etc.
            if (
              code === 'inconsistent_transition_error' ||
              code === 'transition_from_terminal_state_error'
            ) {
              // Client-server desync; resolve by refetching inquiry
              // Does not need session token provided; should be in SessionClientVerify already
              // Don't need to show an error message; should appear like transition succeeded to client
              const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                ApiEndpoint.Inquiry,
                inquiry.id,
              );
              dispatch(routeToNextStep(reloadedInquiry));
            } else {
              throw e;
            }
            break;
          default:
            throw e;
        }
      } else {
        throw e;
      }
    }
  };
}

// this is essentially the same as transitionInquiry, but for verifywith persona step type
// the on complete callback for a successful transition is modified so that a temporary
// success screen is displayed before the inquiry state is updated via the
// InquiryVerifyWithPersonaSuccessRoute
export function transitionInquiryFromVerifyWithPersonaStep(
  inquiry: Inquiry,
  publicKeyWithAssertion: AuthenticationResponseJSON,
  fromComponent: string,
  workflowInitialVariables: WorkflowInitialVariables,
): VerifyThunkAction {
  return async (dispatch) => {
    dispatch(setTransitionStart());
    // Invalidate active pollers, if any
    dispatch(incrementPollerId());

    if (HolderClient.isReusablePersonaPopup && window.opener) {
      window.opener.postMessage({
        name: VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_STARTED,
      });
    }

    try {
      const { data: nextInquiry } = await SessionClientVerify.postResourceAction<
        Inquiry,
        ApiPayload<TransitionWithFieldsPayloadAttributes, ApiModel.Inquiry>
      >(
        {
          id: inquiry.id,
          type: inquiry.type,
          attributes: {
            fields: {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'client-key': publicKeyWithAssertion.response.userHandle,
              'public-key': JSON.stringify(publicKeyWithAssertion),
              'expected-origin': window.location.origin,
            },
            'flow-type': HolderClient.flowType,
          },
        },
        'transition',
        {
          meta: {
            'from-component': fromComponent,
            'from-step': inquiry.attributes['next-step'].name,
            'workflow-initial-variables': workflowInitialVariables,
          },
        },
      );

      // We currently assume that all transitions that transition to the same
      // step NAME should poll. We don't examine the entire step payload because
      // the step payload can change even when we want to poll; for example,
      // submits cause the `prefill` attribute to change due to field values
      // changing.
      const pollingMode = getPollingMode(nextInquiry);

      if (pollingMode === PollingMode.BLOCKING) {
        if (HolderClient.isReusablePersonaPopup && window.opener) {
          window.opener.postMessage({
            name: VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_POLLING,
          });
        }

        await dispatch(
          pollInquiry(nextInquiry, {
            onComplete: (resolve, _reject, inquiry) => {
              if (HolderClient.isReusablePersonaPopup && window.opener) {
                window.opener.postMessage({
                  name: VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_COMPLETED,
                });
              }
              dispatch(setUiReady());
              dispatch(setNextInquiry({ inquiry }));
              if (inquiry.attributes['reusable-persona-status'] === ReusablePersonaStatus.ERRORED) {
                dispatch(replace('/inquiry/verify-with-persona/error'));
              } else {
                dispatch(replace('/inquiry/verify-with-persona/success'));
              }
              resolve();
            },
          }),
        );
      } else {
        if (HolderClient.isReusablePersonaPopup && window.opener) {
          window.opener.postMessage({
            name: VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_COMPLETED,
          });
        }
        dispatch(setUiReady());
        dispatch(setNextInquiry({ inquiry: nextInquiry }));
        if (nextInquiry.attributes['reusable-persona-status'] === ReusablePersonaStatus.ERRORED) {
          dispatch(replace('/inquiry/verify-with-persona/error'));
        } else {
          dispatch(replace('/inquiry/verify-with-persona/success'));
        }
      }
    } catch (e) {
      if (HolderClient.isReusablePersonaPopup && window.opener) {
        window.opener.postMessage({
          name: VERIFY_WITH_PERSONA_MESSAGES.TRANSITION_ERRORED,
        });
      }
      dispatch(setUiReady());
      if (e instanceof ApiNetworkError) {
        // It's possible the request succeeded but the response dropped. This
        // may cause an InconsistentTransitionError down the line.
        // Attempt to resolve by refetching inquiry. If the request failed it
        // will be a no-op.
        const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
          ApiEndpoint.Inquiry,
          inquiry.id,
        );
        dispatch(routeToNextStep(reloadedInquiry));
        // Still show an error message because the problem may be unrelated.
        throw e;
      } else if (e instanceof ApiResponseError) {
        const code = e.errors?.[0].code;
        switch (e.status) {
          case 401: // Unauthorized
            // Expect to be redirected, do nothing
            break;
          case 409: // Conflict -- inconsistent transition, etc.
            if (
              code === 'inconsistent_transition_error' ||
              code === 'transition_from_terminal_state_error'
            ) {
              // Client-server desync; resolve by refetching inquiry
              // Does not need session token provided; should be in SessionClientVerify already
              // Don't need to show an error message; should appear like transition succeeded to client
              const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                ApiEndpoint.Inquiry,
                inquiry.id,
              );
              dispatch(routeToNextStep(reloadedInquiry));
            } else {
              throw e;
            }
            break;
          default:
            throw e;
        }
      } else {
        throw e;
      }
    }
  };
}

export function transitionBack(inquiry: Inquiry): VerifyThunkAction {
  return async (dispatch) => {
    dispatch(setTransitionStart());
    // Invalidate active pollers, if any
    dispatch(incrementPollerId());

    try {
      const { data: nextInquiry } = await SessionClientVerify.postResourceAction<Inquiry>(
        {
          id: inquiry.id,
          type: inquiry.type,
        },
        'transition-back',
        {
          meta: {
            'from-step': inquiry.attributes['next-step'].name,
          },
        },
      );

      dispatch(setUiReady());
      dispatch(routeToNextStep(nextInquiry, { back: true }));
    } catch (e) {
      dispatch(setUiReady());
      if (e instanceof ApiNetworkError) {
        // It's possible the request succeeded but the response dropped. This
        // may cause an InconsistentTransitionError down the line.
        // Attempt to resolve by refetching inquiry. If the request failed it
        // will be a no-op.
        const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
          ApiEndpoint.Inquiry,
          inquiry.id,
        );
        dispatch(routeToNextStep(reloadedInquiry));
        // Still show an error message because the problem may be unrelated.
        throw e;
      } else if (e instanceof ApiResponseError) {
        const code = e.errors?.[0].code;
        switch (e.status) {
          case 401: // Unauthorized
            // Expect to be redirected, do nothing
            break;
          case 409: // Conflict -- inconsistent transition, etc.
            if (
              code === 'inconsistent_transition_error' ||
              code === 'transition_from_terminal_state_error'
            ) {
              // Client-server desync; resolve by refetching inquiry
              // Does not need session token provided; should be in SessionClientVerify already
              // Don't need to show an error message; should appear like transition succeeded to client
              const { data: reloadedInquiry } = await SessionClientVerify.getResource<Inquiry>(
                ApiEndpoint.Inquiry,
                inquiry.id,
              );
              dispatch(routeToNextStep(reloadedInquiry));
            } else {
              throw e;
            }
            break;
          default:
            throw e;
        }
      } else {
        throw e;
      }
    }
  };
}

/**
 * @name routeToNextStep
 *
 * Handler for translating inquiry VSM step names to frontend routes.
 * Used in InquiryUiRoute.
 */
export function routeToNextStep(
  inquiry: Inquiry,
  opts: {
    back?: boolean;
    skipAnimation?: boolean;
    isSameStep?: boolean;
    transition?: PageTransition;
    historyToClear?: MemoryHistory;
  } = {},
): VerifyThunkAction {
  return (dispatch) => {
    // We assume this saga is always passed the most up to date inquiry
    dispatch(saveInquiry(inquiry));

    // don't replace route/reinitialize inquiry if we are routing to same step
    if (opts.isSameStep) {
      return;
    }

    // If passed historyToClear, truncates history to last entry before routing to next step.
    // This is used by combined steps to reset the history stack before handing off to the next step.
    if (opts.historyToClear) {
      truncateMemoryHistory(opts.historyToClear);
    }

    // This won't actually change the route most of the time, but will
    // change the route hash. We use the route hash to trigger page
    // transitions, so this is needed.
    // We don't need to account for preset step types here like gov ID.
    // That is handled in InquiryUiView.
    dispatch(
      replace('/inquiry/ui', {
        back: !!opts.back,
        skipAnimation: !!opts.skipAnimation,
        transition: opts.transition,
      }),
    );
  };
}

/**
 * Notify backend that user canceled their inquiry session.
 *
 * NOTE: This may be called multiple times if user resumes and cancels a
 * second time, but should ideally only be called once per session.
 */
export function onCancel() {
  return SessionClientVerify.onCancel().catch((err) => {
    captureException(err, 'warning', {
      note: 'A fetch exception may imply the fetch library we are using (in react-native?) is invalid',
    });
  });
}

function toSessionError(error: ClientError): InquirySessionError {
  switch (error.code) {
    case 'unauthenticated':
      return InquirySessionError.Permissions;
    default:
      return InquirySessionError.Other;
  }
}

/**
 * A naive error serializer.
 *
 * Consider https://www.npmjs.com/package/serialize-error if we want
 * something more robust
 */
function serializeError(error?: Error): Json | undefined {
  if (!error) return;

  return {
    name: error?.name,
    message: error?.message,
    // IMO this level of information is best collected through Sentry
    // stack: error?.stack,
  };
}

/**
 * Report inquiry session errors
 */
export function onError(clientErr: ClientError, error?: Error) {
  try {
    HolderClient.onError(clientErr);
    EventsClient.logAmplitude('inquiry.error', clientErr);
    if (SessionClientVerify.accessToken) {
      // error endpoint is authenticated
      SessionClientVerify.onError({
        type: toSessionError(clientErr),
        description: serializeError(error),
      });
    }
    addBreadcrumb({
      level: 'error',
      message: 'HolderClient.onError',
      data: {
        status: clientErr.status,
        code: clientErr.code,
        message: clientErr.message,
        description: serializeError(error),
      },
    });
  } catch (err) {
    captureException(err, 'warning');
  }
}

/**
 * Non-saga helpers. Everything here is identical to thunk-actions/inquiries.ts
 * and is duplicated to avoid circular dependency.
 */
async function createSession(inquiry: Inquiry): Promise<ApiResponse<InquirySession>> {
  const inquirySessionResponse = await SessionClientVerify.postResourceCreate<InquirySession>(
    {
      type: ApiModel.InquirySession,
      attributes: {
        'flow-type': HolderClient.flowType,
      },
    },
    {
      meta: {
        'inquiry-id': inquiry.id,
      },
    },
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  SessionClientVerify.accessToken = inquirySessionResponse.meta['access-token'];

  return inquirySessionResponse;
}

/**
 * Captures and records the user's browser and device fingerprint onto the current inquiry session
 */
// TODO: [PER-2713] Add a test for the verify entry points in the spec to test that we actually
//       tried to record a browser fingerprint
async function captureFingerprints(
  allowDeviceFingerprint: boolean,
  country: string,
): Promise<void> {
  async function run(): Promise<void> {
    try {
      const [browserFingerprintResult, deviceFingerprintResult] = await Promise.all([
        computeBrowserFingerprint(),
        allowDeviceFingerprint ? computeDeviceFingerprint(country) : null,
      ]);

      await SessionClientVerify.updateResourceSingleton<
        ApiResource<
          InquirySessionAttributes,
          InquirySessionRelationships,
          ApiModel.CurrentInquirySession
        >
      >(
        {
          attributes: {
            'flow-type': HolderClient.flowType,
          },
          type: ApiModel.CurrentInquirySession,
        },
        {
          meta: {
            'browser-fingerprint': browserFingerprintResult,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'device-fingerprint':
              deviceFingerprintResult == null || deviceFingerprintResult.visitorId === 'n/a'
                ? null
                : deviceFingerprintResult.visitorId,
            'device-fingerprint-components': deviceFingerprintResult,
          },
        },
      );
    } catch (e) {
      // Fingerprinting is not critical to the inquiry flow, and can
      // fail due to reasons out of our control: ignore any error
    }
  }

  run();
}

/**
 * Replicates backend logic in authenticated_controller.rb to find the closest
 * matching locale from the list of enabledLocales
 */
export function findClosestMatchingLocale(
  enabledLocales: string[],
  desiredLocale: string,
  fallback = 'en-US',
) {
  // if no enabled locales (shouldn't happen) or exact match exists, use desired locale
  if (enabledLocales.length === 0 || enabledLocales.includes(desiredLocale)) {
    return desiredLocale;
  }

  // find locale matching same language but different region if exact match doesn't exist
  const matchingLanguage =
    desiredLocale &&
    enabledLocales.find((locale) => locale.split('-')[0] === desiredLocale.split('-')[0]);

  if (matchingLanguage) {
    return matchingLanguage;
  }

  // if fallback locale is enabled use that
  if (enabledLocales.includes(fallback)) {
    return fallback;
  }

  // otherwise take the first enabled locale (most likely going to be ar-EG)
  return enabledLocales[0];
}

/**
 * Sets language based on enabled locales, and existing locale set on inquiry
 * session if it exists.
 *
 * Returns the chosen language.
 */
async function setInitialLanguage(
  i18n: I18n,
  enabledLocales: string[],
  inquirySessionLocale?: string,
): Promise<string> {
  let language: string;

  const localeFromBrowser = findClosestMatchingLocale(enabledLocales, i18n.language);

  // Use locale from session if present so individuals can continue in the same language.
  if (inquirySessionLocale) {
    // fallback should be the locale that most closely matches the current browser locale, which will ultimately
    // fallback to en-US if its enabled or the first enabled locale.
    language = findClosestMatchingLocale(enabledLocales, inquirySessionLocale, localeFromBrowser);
  } else {
    language = localeFromBrowser;
  }

  if (language !== i18n.language) {
    await i18n.changeLanguage(language);
  }

  return language;
}

const DEFAULT_POLL_INTERVAL = 1000;
const DEFAULT_MAX_POLLS = 90;

/**
 * Sets up a timeout that recursively polls and then resolves once complete.
 */
export function pollInquiry(
  currInquiry: Inquiry,
  opts: {
    historyToClear?: MemoryHistory;
    onComplete?: (
      resolve: (value: void | PromiseLike<void>) => void,
      reject: (reason?: unknown) => void,
      inquiry: Inquiry,
    ) => void;
    pollingMode?: PollingMode;
    /**
     * Skips writing the inquiry state at start of polling to Redux.
     * We normally udpate state to ensure we have the latest inquiry state,
     * but we sometimes don't want the UI to change based on polling.
     *
     * Reusable Persona polling skips this because inquiry next step updates
     * during rehydration, but we don't want the client to refresh.
     */
    skipInquiryStateUpdateOnPoll?: boolean;
  } = {},
): VerifyThunkAction<Promise<void>> {
  return (dispatch, getState) =>
    new Promise<void>((resolve, reject) => {
      const transitionPollingConfig = currInquiry.attributes['wait-for-transition'];

      if (
        !opts.skipInquiryStateUpdateOnPoll &&
        // The first transition which triggers a automated RP-initiated workflow transition
        // will trigger a call to pollInquiry with the step that is being skipped and
        // currently-transitioning-with-persona set to true.
        // e.g. transitioning to a country-select step leading into a government id step
        // that is being skipped via RP will return a polling inquiry on government id step
        // we don't want to update the UI to be on the government id step while we
        // are waiting for the workflow to be run.
        !transitionPollingConfig?.['currently-transitioning-with-persona']
      ) {
        dispatch(saveInquiry(currInquiry));
      }

      let pollTimeout: number;

      const pollingMode = opts.pollingMode ?? getPollingMode(currInquiry);
      const maxPolls = transitionPollingConfig?.['max-attempts'] ?? DEFAULT_MAX_POLLS;

      const onCompleteFromOptions = opts.onComplete
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          (inquiry: Inquiry) => opts.onComplete(resolve, reject, inquiry)
        : null;

      dispatch(incrementPollerId());
      const initialPollerId = getState().ui.pollerId;

      if (pollingMode === PollingMode.BLOCKING) {
        dispatch(setUiBlocked());
      }

      function poll(attemptsRemaining: number, currInquiry: Inquiry) {
        if (attemptsRemaining <= 0) {
          clearTimeout(pollTimeout);
          dispatch(setUiReady());
          captureMessage(`Polled more than ${maxPolls} times without success`, 'error', {
            id: currInquiry.id,
          });
          reject();
          return;
        }

        pollTimeout = window.setTimeout(
          () =>
            pollInquiryProcessHelper({
              inquiry: currInquiry,
              context: {
                initialPollerId,
                pollingMode,
                getPollerId: () => getPollerId(getState()),
              },
              onPending: (nextInquiry: Inquiry) => {
                poll(attemptsRemaining - 1, nextInquiry);
              },
              onComplete:
                onCompleteFromOptions ??
                ((nextInquiry: Inquiry) => {
                  const { historyToClear } = opts;
                  const isSameStep =
                    nextInquiry.attributes['next-step'].name ===
                    currInquiry.attributes['next-step'].name;
                  const serverErrors: { name: string; message: string }[] | undefined =
                    nextInquiry.attributes['next-step'].config?.['server-component-errors'];
                  const serverMessages: string[] | undefined =
                    nextInquiry.attributes['next-step'].config?.['server-step-messages'];
                  if (serverMessages && isSameStep) {
                    for (const serverMessage of serverMessages) {
                      dispatch(showMessage(serverMessage));
                    }
                  }

                  dispatch(setUiReady());
                  dispatch(routeToNextStep(nextInquiry, { isSameStep, historyToClear }));
                  if (serverErrors && isSameStep) {
                    const errorMap = fromPairs(map((v) => [v.name, v.message], serverErrors));
                    reject(new SubmissionError(errorMap));
                  } else {
                    resolve();
                  }
                }),
              onError: (error) => {
                console.error(error);
                clearTimeout(pollTimeout);
                dispatch(setUiReady());
                captureMessage('Failed to poll for inquiry status', 'error', {
                  id: currInquiry.id,
                  error,
                });
                reject();
              },
              onAbort: () => {
                clearTimeout(pollTimeout);
                resolve();
              },
            }),
          // Ensure we use poll interval from currInquiry in case it changes mid-poll
          currInquiry?.attributes?.['wait-for-transition']?.['interval-ms'] ??
            DEFAULT_POLL_INTERVAL,
        );
      }

      poll(maxPolls, currInquiry);
    });
}

interface PollInquiryProcessContext {
  initialPollerId: number;
  pollingMode: PollingMode;
  getPollerId: () => number;
}

interface PollInquiryProcessHelperArgs {
  inquiry: Inquiry;
  context: PollInquiryProcessContext;
  onPending: (nextInquiry: Inquiry) => void;
  onComplete: (nextInquiry: Inquiry) => void;
  onError?: (error: Error) => void;
  onAbort?: () => void;
}

function getPollerId(state: RootState) {
  return state.ui.pollerId;
}

/**
 * Note that unlike the Inquiry v2 pollers, there is no concept of success or
 * failure, only pending/complete/error. Complete will encompass both
 * verification successes and failures, and error is for true error states.
 */
async function pollInquiryProcessHelper({
  inquiry,
  context,
  onPending,
  onComplete,
  onError,
  onAbort,
}: PollInquiryProcessHelperArgs): Promise<void> {
  try {
    const { data: nextInquiry } = await SessionClientVerify.getResource<Inquiry>(
      ApiEndpoint.Inquiry,
      inquiry.id,
      {
        // Don't retry at client level since polling has its own retry config
        retryOptions: {
          retries: 0,
        },
      },
    );

    // Check poller ID after network call, in case it has become out of date
    const isPollerNotCurrent = context.getPollerId() > context.initialPollerId;
    if (isPollerNotCurrent) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onAbort();
      return;
    }

    // We currently assume that all successful workflow transitions must change
    // the step name. In the future, we can allow more flexibility by e.g. checking
    // if a (currently unimplemented) "transition ID" changes across polls.
    const isSameStep =
      nextInquiry.attributes['next-step'].name === inquiry.attributes['next-step'].name;
    if (!isSameStep) {
      // For transitions with persona, a name change doesn't mean that we want to update the frontend
      // Only update the frontend if we are no longer transitioning with persona
      const currentlyTransitioningWithPersona =
        nextInquiry.attributes?.['wait-for-transition']?.['currently-transitioning-with-persona'];
      if (!currentlyTransitioningWithPersona) {
        onComplete(nextInquiry);
        return;
      }
    }

    switch (context.pollingMode) {
      case PollingMode.BACKGROUND:
      case PollingMode.BLOCKING:
        if (context.pollingMode !== getPollingMode(nextInquiry)) {
          // BACKGROUND -> BLOCKING   : New polling mode. Complete current poller.
          // BLOCKING   -> BACKGROUND : New polling mode. Complete current poller.
          // BACKGROUND -> NONE       : Inquiry no longer requesting background polling, stop polling.
          // BLOCKING   -> NONE       : Transition has completed, stop polling.
          onComplete(nextInquiry);
        } else {
          // BACKGROUND -> BACKGROUND : Polling mode hasn't changed, keep polling.
          // BLOCKING   -> BLOCKING   : Polling mode hasn't changed, keep polling.
          onPending(nextInquiry);
        }
        break;
      case PollingMode.NONE:
      default:
        // NONE -> * : Always halt polling if it was never required to begin with.
        onComplete(nextInquiry);
    }
  } catch (err) {
    if (err instanceof ApiNetworkError || (err instanceof ApiResponseError && err.status === 502)) {
      // Continue polling through network and intermittent errors
      onPending(inquiry);
    } else {
      if (onError != null) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onError(err);
      } else {
        throw err;
      }
    }
  }
}
